import { useState, useCallback, useMemo } from "react";
import { Text, Flex, Code, Button, Spinner } from "@radix-ui/themes";
import { useApi, apiResolver } from "@/api/useApi";
import { useQuery, useMutation } from "react-query";
import { Release, Simulator } from "@/api/simulator/simulator.model";
import Select, { SelectOption } from "@/components/shared/select";
import { toast } from "sonner";
import { useGetAccess } from "@/v2/entities/access/Access.api";
import { SelectSimulation } from "@/api/simulator/SelectSimulation";

function buildWorkspaceSelectOptions(workspaces: string[]): SelectOption[] {
  return workspaces.map((workspace) => ({
    label: workspace,
    value: workspace,
  }));
}

function buildReleaseSelectOptions(releases: Release[]): SelectOption[] {
  const sortedReleases = [...releases].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  return sortedReleases
    .map((release, index) => ({
      label: `Release ${release.id}`,
      value: String(release.id),
      icon: (
        <Code size="2" color="gray" mr="2">
          v{index + 1}
        </Code>
      ),
    }))
    .reverse();
}

type CopyReleasePayload = {
  sourceReleaseId: string;
  targetReleaseId: string;
  sourceTenantId: string;
  targetTenantId: string;
};

interface CopyReleaseProps {
  onClose: () => void;
}

export function CopyRelease({ onClose }: CopyReleaseProps) {
  const { api } = useApi();

  const [sourceWorkspace, setSourceWorkspace] = useState<string | null>(null);
  const [sourceSimulatorId, setSourceSimulatorId] = useState<number | null>(
    null
  );
  const [sourceReleaseId, setSourceReleaseId] = useState<string | null>(null);
  const [targetWorkspace, setTargetWorkspace] = useState<string | null>(null);
  const [targetSimulatorId, setTargetSimulatorId] = useState<number | null>(
    null
  );

  // load workspaces list
  const { data: workspaces } = useGetAccess(true);

  // load simulators list
  const { data: sourceSimulators, isLoading: isLoadingSourceSimulators } =
    useQuery({
      queryKey: ["admin-dashboard", "simulators", sourceWorkspace],
      queryFn: () =>
        apiResolver<Simulator[]>(
          api.get("/platform/simulators", {
            headers: {
              "x-tenant-id": sourceWorkspace,
            },
          })
        ),
      enabled: sourceWorkspace != null,
    });

  // load source releases list
  const { data: sourceReleases, isLoading: isLoadingSourceReleases } = useQuery(
    {
      queryKey: [
        "admin-dashboard",
        "source-releases",
        sourceSimulatorId,
        sourceWorkspace,
      ],
      queryFn: () =>
        api.get<Release[]>(
          `/platform/simulators/${sourceSimulatorId}/releases`,
          {
            headers: {
              "x-tenant-id": sourceWorkspace,
            },
          }
        ),
      enabled: sourceSimulatorId != null && sourceWorkspace != null,
    }
  );

  // load target simulators list
  const { data: targetSimulators, isLoading: isLoadingTargetSimulators } =
    useQuery({
      queryKey: ["admin-dashboard", "simulators", targetWorkspace],
      queryFn: () =>
        apiResolver<Simulator[]>(
          api.get("/platform/simulators", {
            headers: {
              "x-tenant-id": targetWorkspace,
            },
          })
        ),
      enabled: targetWorkspace != null,
    });

  // load target releases list
  const { data: targetReleases, isLoading: isLoadingTargetReleases } = useQuery(
    {
      queryKey: [
        "admin-dashboard",
        "target-releases",
        targetSimulatorId,
        targetWorkspace,
      ],
      queryFn: () =>
        api.get<Release[]>(
          `/platform/simulators/${targetSimulatorId}/releases`,
          {
            headers: {
              "x-tenant-id": targetWorkspace,
            },
          }
        ),
      enabled: targetSimulatorId != null && targetWorkspace != null,
    }
  );

  const workspacesOptions = useMemo<SelectOption[]>(() => {
    return buildWorkspaceSelectOptions(workspaces?.tenants ?? []);
  }, [workspaces]);

  const sourceReleasesOptions = useMemo<SelectOption[]>(() => {
    return buildReleaseSelectOptions(sourceReleases?.data ?? []);
  }, [sourceReleases]);

  const draftTargetRelease = useMemo(() => {
    return targetReleases?.data?.find((r) => r.status === "draft");
  }, [targetReleases]);

  const { mutate: copyRelease, isLoading: isCopyingRelease } = useMutation({
    mutationFn: (payload: CopyReleasePayload) => {
      return api.post(
        `platform/admin-dashboard/releases/${payload.sourceReleaseId}/copy-to/${payload.targetReleaseId}`,
        {
          sourceTenantId: payload.sourceTenantId,
          targetTenantId: payload.targetTenantId,
        }
      );
    },
    onSuccess: () => {
      toast.success("Release copied successfully");
      onClose();
    },
    onError: () => {
      toast.error("Failed to copy release");
    },
  });

  const handleSubmit = useCallback(() => {
    if (!sourceReleaseId || !draftTargetRelease?.id) {
      return;
    }

    copyRelease({
      sourceReleaseId: sourceReleaseId,
      sourceTenantId: sourceWorkspace ?? "",
      targetTenantId: targetWorkspace ?? "",
      targetReleaseId: draftTargetRelease.id.toString(),
    });
  }, [
    sourceSimulatorId,
    sourceReleaseId,
    draftTargetRelease?.id,
    sourceWorkspace,
    targetWorkspace,
  ]);

  const isSubmitDisabled =
    !sourceSimulatorId || !sourceReleaseId || !targetSimulatorId;

  return (
    <Flex p={"4"} direction="column" gap="6">
      {/* Source Section */}
      <Flex direction="column" gap="4">
        <Text size="4" weight="bold" color="gray">
          Source
        </Text>
        <Select
          placeholder="Select source workspace"
          options={workspacesOptions}
          value={sourceWorkspace ?? undefined}
          onChange={(value) => {
            setSourceWorkspace(value);
            setSourceSimulatorId(null);
            setSourceReleaseId(null);
          }}
          // disabled={isLoadingWorkspaces}
        />
        <SelectSimulation
          placeholder="Select source simulator"
          simulators={sourceSimulators}
          value={sourceSimulatorId ?? undefined}
          onChange={(value) => {
            setSourceSimulatorId(value);
            setSourceReleaseId(null);
          }}
          disabled={isLoadingSourceSimulators || !sourceWorkspace}
        />

        <Select
          placeholder="Select source release to copy"
          options={sourceReleasesOptions}
          value={sourceReleaseId ?? undefined}
          onChange={(value) => setSourceReleaseId(value)}
          disabled={isLoadingSourceReleases || !sourceSimulatorId}
        />
      </Flex>

      {/* Target Section */}
      <Flex direction="column" gap="4">
        <Text size="4" weight="bold" color="gray">
          Target
        </Text>
        <Select
          placeholder="Select target workspace"
          options={workspacesOptions}
          value={targetWorkspace ?? undefined}
          onChange={(value) => {
            setTargetWorkspace(value);
            setTargetSimulatorId(null);
          }}
          disabled={isLoadingTargetSimulators || !sourceReleaseId}
        />
        <SelectSimulation
          placeholder="Select target simulator"
          simulators={targetSimulators}
          value={targetSimulatorId ?? undefined}
          onChange={(value) => {
            setTargetSimulatorId(value);
          }}
          disabled={isLoadingTargetSimulators || !targetWorkspace}
        />
      </Flex>

      {/* Copy Button */}
      <Button
        size="3"
        color="yellow"
        variant="solid"
        onClick={handleSubmit}
        disabled={isSubmitDisabled || isCopyingRelease}
      >
        <Spinner loading={isCopyingRelease} />
        Copy
      </Button>
    </Flex>
  );
}
